import cn from 'classnames';
import map from 'lodash/map';
import { useKeenSlider } from 'keen-slider/react';
import { useEffect, useRef, useState } from 'react';
import SVG from 'react-inlinesvg';
import ImageComponent from '../../Image/Image';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import 'keen-slider/keen-slider.scss';
import styles from './SliderModalGallery.module.scss';
import gsap from 'gsap';

export const SliderModalGallery = ({ className, slides }) => {
    const breakpoint = useBreakpoint();
    const [ activeIndex, setActiveIndex ] = useState(0);
    const [ loaded, setLoaded ] = useState(false);
    const sliderParams = useRef({
        initial: 0,
        drag: false,
        mode: 'snap',
        slideChanged(slider) {
            setActiveIndex(slider.track.details.rel)
        },
        created() {
            setLoaded(true)
        },
    })
    const [ sliderRef, instanceRef ] = useKeenSlider(sliderParams.current);
    const controlsRef = useRef(null);
    const directionRef = useRef(null);

    useEffect(() => {
        const _slider = instanceRef.current;

        if(_slider) {
            _slider.update();

            return () => {
                setActiveIndex(0);
                _slider.destroy();
            }
        }
    }, [className, instanceRef, slides]);

    useEffect(() => {
        if(instanceRef.current) {
            sliderParams.current = {
                ...sliderParams.current,
                ...{
                    drag: breakpoint === 'mobile'
                }
            }

            instanceRef.current.update(sliderParams.current);
        }
    }, [breakpoint, instanceRef])

    useEffect(() => {
        const container = controlsRef.current;
        const button = directionRef.current;
        const slider = instanceRef.current;

        if(breakpoint === 'desktop' && loaded && container && button && slider) {
            let direction = null;

            const disabledToggle = () => {
                const isDisabled = direction === 1
                    ? slider.track.details.progress === 1 ? true : false
                    : slider.track.details.progress === 0 ? true : false

                gsap.to(button, { opacity: isDisabled ? .3 : 1 })
            }

            const calcPosition = (event) => {
                const rect = event.currentTarget.getBoundingClientRect();

                const x = event.clientX - rect.x;
                direction = x > rect.width / 2 ? 1 : 0

                gsap.set(button, {
                    left: x,
                    top: event.clientY - rect.x,
                    rotate: direction === 1 ? 0 : -180,
                })

                disabledToggle();
            }

            const handlerMouseEnter = (event) => {
                calcPosition(event);

                gsap.to(button, { autoAlpha: 1, duration: .3 });
            }

            const handlerMouseLeave = () => {
                gsap.to(button, { autoAlpha: 0, duration: .1 });

                direction = null;
            }

            const handlerMouseDown = (event) => {
                event.stopPropagation();
                
                if(direction !== null) {
                    slider.on('animationEnded', disabledToggle)

                    if(direction === 1) {
                        slider.next();
                    } else {
                        slider.prev();
                    }
                }
            }

            container.addEventListener('mouseenter', handlerMouseEnter);
            container.addEventListener('mousemove', calcPosition);
            container.addEventListener('mouseleave', handlerMouseLeave);
            container.addEventListener('mousedown', handlerMouseDown);

            return () => {
                container.removeEventListener('mouseenter', handlerMouseEnter);
                container.removeEventListener('mousemove', calcPosition);
                container.removeEventListener('mouseleave', handlerMouseLeave);
                container.removeEventListener('mousedown', handlerMouseDown);

                gsap.set(button, { clearProps: 'all' });
            }
        }
    }, [controlsRef, directionRef, loaded, instanceRef, breakpoint])

    return (
        <div className={ cn(className, styles.wrapper) }>
            <div
                className={ cn('keen-slider', styles.slider) }
                ref={ sliderRef }
            >
                { map(slides, (item, index) => (
                    <div
                        key={ index }
                        className={ cn('keen-slider__slide', styles.slide) }
                    >
                        <ImageComponent src={ item } />
                    </div>
                )) }
            </div>
            { (loaded && instanceRef.current) && (
                <>
                    { breakpoint === 'desktop' && (
                        <div ref={ controlsRef } className={ styles.controls }>
                            <div ref={ directionRef } className={ styles.direction }>
                                <SVG src="/img/icons/icon-slider-button.svg" />
                            </div>
                        </div>
                    ) }
                    <div className={ styles.pagination }>
                        { map(instanceRef.current.track.details.slides, (_, index) => (
                            <div
                                key={ index }
                                className={ cn(
                                    styles.dot, {
                                        [styles.dotActive]: index === activeIndex
                                    }
                                ) }
                                onClick={ () => instanceRef.current?.moveToIdx(index) }
                            />
                        )) }
                    </div>
                </>
            ) }
        </div>
    )
}